import { useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
} from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useAuth from "../../hooks/useAuth";
import useSettings from "../../hooks/useSettings";
import Logo from "../Logo";
import LogoBlanc from "../LogoBlanc";
import Scrollbar from "../Scrollbar";
import NavSection from "../NavSection";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { sectionsAdmin, sections2, sections3, sections4 } from "./sidebarSections";

const DashboardSidebar = ({ onMobileClose, openMobile, drawerOpen, setDrawerOpen }) => {
  const location = useLocation();
  const { user } = useAuth();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const settings = useSettings();

  // Gestion de l'état du drawer
  const toggleDrawer = () => {
    const newState = !drawerOpen;
    setDrawerOpen(newState);
    localStorage.setItem("drawerOpen", JSON.stringify(newState));
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  useEffect(() => {
    const storedDrawerOpen = localStorage.getItem("drawerOpen");
    if (storedDrawerOpen !== null) {
      setDrawerOpen(JSON.parse(storedDrawerOpen));
    }
  }, [setDrawerOpen]);


  // Fonction pour déterminer les sections dynamiquement
  const getSections = () => {
    if (user.access_level === 0) return sectionsAdmin;
    if (user.id_service === 3) return sections3;
    if (user.id_service === 4) return sections4;
    return sections2;
  };

  // Le "Suivi des tâches" et le "Suivi inscrits/désinscrits" ne sont visibles que
  // pour les services Relance (id_service 4) et Administrateur (access_level 0).
  const canSeeTaskTracking = user.access_level === 0 || user.id_service === 4;
  const RESTRICTED_TASK_PATHS = ["/dashboard/tasks-tracking", "/dashboard/learners-tracking"];
  const filterTaskTracking = (sections) => {
    if (canSeeTaskTracking) return sections;
    return sections.map((section) => ({
      ...section,
      items: Array.isArray(section.items)
        ? section.items.filter((item) => !RESTRICTED_TASK_PATHS.includes(item.path))
        : section.items,
    }));
  };

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: 'relative',
        backgroundColor: settings.settings.theme === "LIGHT" 
          ? "rgba(27, 36, 69, 0.98)" // Dark premium blue from primary.main but richer
          : "rgba(28, 37, 49, 0.98)", // Dark theme surface
        backdropFilter: 'blur(20px)',
        borderRight: `1px solid ${settings.settings.theme === "LIGHT" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.05)"}`,
      }}
    >
      {lgUp && (
        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: 'absolute',
            top: 24,
            right: -16,
            width: 32,
            height: 32,
            backgroundColor: settings.settings.theme === "LIGHT" ? '#ffffff' : '#293142',
            border: `1px solid ${settings.settings.theme === "LIGHT" ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            color: settings.settings.theme === "LIGHT" ? '#64748b' : '#94a3b8',
            zIndex: 10,
            '&:hover': {
              backgroundColor: settings.settings.theme === "LIGHT" ? '#f8fafc' : '#334155',
              color: 'primary.main',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s'
          }}
        >
          {drawerOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      )}

      <Scrollbar options={{ suppressScrollX: true }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: drawerOpen ? 3 : 2,
            transition: 'padding 0.3s'
          }}
        >
          <RouterLink to="/">
              <LogoBlanc sx={{ height: drawerOpen ? 120 : 50, width: drawerOpen ? 120 : 50, transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }} />
          </RouterLink>
        </Box>
        <Divider sx={{ mb: 2, opacity: 0.1, borderColor: '#ffffff' }} />
        
        <Box sx={{ p: drawerOpen ? 2 : 1, transition: 'padding 0.3s' }}>
          {filterTaskTracking(getSections()).map((section) => (
            <NavSection
              key={section.title}
              pathname={location.pathname}
              sx={{ "& + &": { mt: drawerOpen ? 3 : 2 } }}
              {...section}
              drawerOpen={drawerOpen}
              openMobile={openMobile}
            />
          ))}
        </Box>
      </Scrollbar>
    </Box>
  );

  if (lgUp) {
    return (
      <Box>
        <Drawer
          anchor="left"
          onClose={toggleDrawer}
          open={drawerOpen}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent', // Handled by content Box
              height: "calc(100% - 64px) !important",
              top: "64px !Important",
              width: drawerOpen ? "280px" : "88px",
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'visible', // For the floating button
              borderRight: 'none',
              boxShadow: '4px 0 24px 0 rgba(0,0,0,0.05)'
            },
          }}
          variant="permanent"
        >
          {content}
        </Drawer>
      </Box>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      PaperProps={{
        sx: { 
          backgroundColor: 'transparent',
          width: 280,
          borderRight: 'none'
        },
      }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  setDrawerOpen: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
};

export default DashboardSidebar;
